import { useState, useEffect, useMemo } from "react";
import { getTypedVal, setObjectURL, setStateURL } from "../../../utils/urls";
import type { IFamilyConfigCallBack } from "../../../types";
import type { IFamilies, IFamilyFilters } from "../types";
import { getUniqueColors, getUniqueFlux, getUniqueOptics } from "../familyUtils";

type UseFamilyStateProps = {
  families: IFamilies;
  familyConfig: string[];
  familyConfigCallback: IFamilyConfigCallBack;
};

type UseFamilyStateReturn = {
  searchValue: string;
  selectedFamilyIds: string[];
  familyFilters: IFamilyFilters;
  colorArr: number[];
  fluxArr: number[];
  opticsArr: string[];
  searchHandler: (value: string) => void;
  radioHandler: (value: string, index: number) => void;
  handleDelete: (familyId: string) => void;
  addFilter: (key: keyof IFamilyFilters, value: number[] | string[]) => void;
  isDisabled: (id: string) => boolean;
};

export function useFamilyState({ families, familyConfig, familyConfigCallback }: UseFamilyStateProps): UseFamilyStateReturn {
  const familiesList = families.data.families;

  // Derive unique filter option arrays from the loaded families data.
  // Memoized so they are only recomputed when the families list reference changes.
  const colorArr = useMemo(() => getUniqueColors(familiesList), [familiesList]);
  const fluxArr = useMemo(() => getUniqueFlux(familiesList), [familiesList]);
  const opticsArr = useMemo(() => getUniqueOptics(familiesList), [familiesList]);

  // Build the initial filter state from URL params (for deep-link / page reload support).
  // Memoized so it is computed once per families change, not on every render.
  // flux guards against empty fluxArr to avoid Infinity / -Infinity from Math.min/max.
  const initFilters = useMemo<IFamilyFilters>(() => {
    const initial_color_value = getTypedVal("color", "");
    const initial_flux_value = getTypedVal("flux", "");
    const initial_optic_value = getTypedVal("optic", "");

    const fluxRange: [number, number] =
      fluxArr.length >= 2 ? [Math.min(...fluxArr), Math.max(...fluxArr)] : fluxArr.length === 1 ? [fluxArr[0], fluxArr[0]] : [0, 0];

    return {
      color: initial_color_value === "" ? colorArr : initial_color_value.split(",").map(Number),
      flux: initial_flux_value === "" ? fluxRange : initial_flux_value.split(",").map(Number),
      optic: initial_optic_value === "" ? opticsArr : initial_optic_value.split(","),
    };
  }, [colorArr, fluxArr, opticsArr]);

  // Lazy initializer reads the URL param only once on mount.
  const [searchValue, setSearchValue] = useState<string>(() => getTypedVal("search_value", ""));
  // selectedFamilyIds holds the canonical list of selected family IDs (max 3).
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [familyFilters, setFamilyFilters] = useState<IFamilyFilters>(initFilters);

  // Sync state when the families data or the external familyConfig prop changes
  // (e.g. on initial data load or when the parent resets the selection).
  // selectedFamilyIds is intentionally excluded from deps: the effect sets it, so including
  // it would cause a double-run on every selection change.
  useEffect(() => {
    setSelectedFamilyIds(familyConfig ?? []);
    setFamilyFilters(initFilters);
    setSearchValue(getTypedVal("search_value", ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [families, familyConfig]);

  // Updates a single filter key and persists the new value to the URL.
  const addFilter = (key: keyof IFamilyFilters, value: number[] | string[]): void => {
    setFamilyFilters((prev) => ({ ...prev, [key]: value }));
    setStateURL(key, value.join(","));
  };

  // Toggles a family's selection state.
  // Selecting is capped at 3 families; deselecting removes from the id list.
  // Notifies the parent and persists the updated selection to the URL.
  const radioHandler = (familyId: string, _index: number): void => {
    const isSelected = selectedFamilyIds.includes(familyId);

    // Ignore clicks on unselected cards when the 3-family limit is already reached.
    if (!isSelected && selectedFamilyIds.length >= 3) return;

    const nextIds = isSelected ? selectedFamilyIds.filter((id) => id !== familyId) : [...selectedFamilyIds, familyId];

    setSelectedFamilyIds(nextIds);
    familyConfigCallback({ familyIds: nextIds });
    setObjectURL({ family_ids: nextIds.toString() });
  };

  // Updates the search term in state and URL, then re-notifies the parent
  // with the current selectedFamilyIds so downstream steps stay consistent.
  const searchHandler = (value: string): void => {
    familyConfigCallback({ familyIds: selectedFamilyIds });
    if (typeof value === "string") setStateURL("search_value", value);
    setSearchValue(value);
  };

  // Removes a family from the selection via the chip delete button and syncs the URL.
  const handleDelete = (familyId: string): void => {
    const newIds = selectedFamilyIds.filter((id) => id !== familyId);
    setSelectedFamilyIds(newIds);
    familyConfigCallback({ familyIds: newIds });
    setObjectURL({ family_ids: newIds.toString() });
  };

  // A family card checkbox is disabled when 3 are already selected and this one is not among them.
  const isDisabled = (id: string): boolean => {
    if (selectedFamilyIds.length < 3) return false;
    return !selectedFamilyIds.includes(id);
  };

  return {
    searchValue,
    selectedFamilyIds,
    familyFilters,
    colorArr,
    fluxArr,
    opticsArr,
    searchHandler,
    radioHandler,
    handleDelete,
    addFilter,
    isDisabled,
  };
}
